import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { Layers, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { useFeatureCountsByComponent } from "@/application/use-cases/features/use-feature-counts.queries";
import {
  MAP_DOMAIN_COLOR,
  MAP_DOMAIN_COLOR_SOFT,
  type ArchitectureMapNode,
} from "@/presentation/data/architecture-map-data";
import type { MapNodeData } from "@/presentation/hooks/use-architecture-map-page";

type ComponentMapNodeType = Node<MapNodeData, "mapComponent">;

function OwnershipBadge({ data }: { data: ArchitectureMapNode }) {
  if (data.kind === "actor") {
    return (
      <span
        data-slot="map-actor-badge"
        title="People or organizational boundary — not a vendor product"
        className="font-data inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase"
        style={{ background: "oklch(0.52 0.09 35)" }}
      >
        People
      </span>
    );
  }
  const isYandex = data.ownership === "yandex";
  return (
    <span
      data-slot="map-ownership-badge"
      title={
        isYandex
          ? "Yandex-owned product in the stack — Yango configures, does not host the codebase"
          : "External vendor or Israeli integration"
      }
      className={cn(
        "font-data inline-flex max-w-[112px] flex-col items-end gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide uppercase",
        isYandex ? "bg-[oklch(0.55_0.17_190)] text-white" : "bg-[oklch(0.4_0.02_240)] text-white"
      )}
    >
      <span className="inline-flex items-center gap-1">
        <span className="size-1 shrink-0 rounded-full bg-white" />
        {isYandex ? "Yandex" : "External"}
      </span>
      {isYandex ? (
        <span className="text-[7px] leading-tight font-semibold normal-case opacity-90">
          Platform stack
        </span>
      ) : null}
    </span>
  );
}

function HighlightToggle({
  id,
  active,
  onToggle,
}: {
  id: string;
  active: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <button
      type="button"
      data-slot="map-highlight-toggle"
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onToggle(id);
      }}
      title={active ? "Remove from this stage" : "Add to this stage"}
      aria-pressed={active}
      className={cn(
        "nodrag nopan inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors",
        active
          ? "text-[oklch(0.55_0.17_190)] hover:text-[oklch(0.45_0.18_195)]"
          : "text-foreground/40 hover:text-foreground/80"
      )}
    >
      <Star
        className="size-3.5"
        strokeWidth={active ? 2 : 1.75}
        fill={active ? "currentColor" : "none"}
      />
    </button>
  );
}

function EditableLabel({
  id,
  name,
  onRename,
}: {
  id: string;
  name: string;
  onRename: (id: string, next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.textContent = name;
      ref.current.focus();
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [editing, name]);

  function commit() {
    const next = (ref.current?.textContent ?? "").trim();
    setEditing(false);
    if (next.length > 0 && next !== name) onRename(id, next);
  }

  return (
    <span
      ref={ref}
      role="textbox"
      title="Double-click to rename"
      contentEditable={editing}
      suppressContentEditableWarning
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      onMouseDown={(e) => {
        if (editing) e.stopPropagation();
      }}
      onPointerDown={(e) => {
        if (editing) e.stopPropagation();
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLElement).blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          if (ref.current) ref.current.textContent = name;
          (e.target as HTMLElement).blur();
        }
      }}
      className={cn(
        "nodrag min-w-0 flex-1 text-[12px] leading-snug font-bold tracking-tight break-words outline-none",
        editing && "rounded bg-white px-1 ring-1 ring-[oklch(0.65_0.13_55)]"
      )}
    >
      {editing ? null : name}
    </span>
  );
}

/**
 * Read-only feature-count badge. Shown when a system node has 1+ features
 * bound via `features.component_id`. Click-through is deferred — features are
 * managed on the kanban boards, not the map.
 */
function FeatureCountBadge({ count }: { count: number }) {
  return (
    <span
      data-slot="feature-count-badge"
      title={`${count} feature${count === 1 ? "" : "s"} mapped to this component`}
      className="font-data inline-flex items-center gap-0.5 rounded bg-[oklch(0.72_0.17_190_/_0.14)] px-1 py-0.5 text-[9px] font-bold text-[oklch(0.4_0.16_195)] tabular-nums"
    >
      <Layers className="size-2.5" />
      {count}
    </span>
  );
}

function NodeHeader({ data, featureCount }: { data: MapNodeData; featureCount: number }) {
  return (
    <div data-slot="map-node-header" className="flex flex-col gap-2">
      <div className="flex items-start gap-1.5">
        <HighlightToggle id={data.id} active={data.active} onToggle={data.onToggleHighlight} />
        {data.onRename ? (
          <EditableLabel id={data.id} name={data.name} onRename={data.onRename} />
        ) : (
          <span className="min-w-0 flex-1 text-[12px] leading-snug font-bold tracking-tight break-words">
            {data.name}
          </span>
        )}
        {data.kind === "system" && featureCount > 0 ? (
          <FeatureCountBadge count={featureCount} />
        ) : null}
      </div>
      <div className="flex justify-end">
        <OwnershipBadge data={data} />
      </div>
    </div>
  );
}

function NodeBody({ data }: { data: MapNodeData }) {
  return (
    <div
      data-slot="map-node-body"
      className="mt-2 flex flex-col gap-1.5 border-t border-black/10 pt-2"
    >
      <p className="text-foreground/80 text-[10px] leading-snug font-medium">{data.role}</p>
      {data.modules && data.modules.length > 0 ? (
        <ul className="text-foreground/70 list-inside list-disc text-[9px] leading-snug">
          {data.modules.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      ) : null}
      {data.note ? (
        <p className="text-foreground/60 text-[9px] leading-snug italic">{data.note}</p>
      ) : null}
    </div>
  );
}

export function ComponentMapNode({ data, selected }: NodeProps<ComponentMapNodeType>) {
  const active = data.active;
  const accent = MAP_DOMAIN_COLOR[data.domain];
  const fill = MAP_DOMAIN_COLOR_SOFT[data.domain];
  const isActor = data.kind === "actor";
  const { data: counts } = useFeatureCountsByComponent();
  const featureCount = counts?.[data.id] ?? 0;
  return (
    <div
      data-slot="component-map-node"
      data-active={active}
      data-domain={data.domain}
      data-kind={data.kind}
      style={{
        background: fill,
        borderLeft: `4px solid ${accent}`,
        boxShadow: selected
          ? `0 0 0 2px ${accent}, 0 8px 24px oklch(0 0 0 / 0.14)`
          : `0 1px 2px oklch(0 0 0 / 0.06), 0 4px 12px oklch(0 0 0 / 0.05)`,
      }}
      className={cn(
        "w-max max-w-[min(100vw-2rem,340px)] min-w-[248px] rounded-md px-3 py-2.5 transition-all duration-200",
        active ? "opacity-100" : "opacity-25 grayscale",
        isActor ? "border border-dashed border-black/15" : ""
      )}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!h-2 !w-2 !border-0"
        style={{ background: accent, opacity: 0.55 }}
      />
      <NodeHeader data={data} featureCount={featureCount} />
      {selected ? <NodeBody data={data} /> : null}
      <Handle
        type="source"
        position={Position.Right}
        className="!h-2 !w-2 !border-0"
        style={{ background: accent, opacity: 0.55 }}
      />
    </div>
  );
}
